import { css } from "@emotion/css";
import { Button, RadioButtonGroup, RadioButton } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { type ProjectIdAndName, type ProjectEphemeralEnvironmentDetail, type ProjectResource, type Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { ProjectChip } from "~/components/Chips/index";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { Note } from "~/components/form/index";
type DeprovisioningEnvironmentDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    project: ProjectResource;
    reloadEnvironmentsForProject: () => void;
};
export function DeprovisionEnvironmentDialog({ environment, project, reloadEnvironmentsForProject }: DeprovisioningEnvironmentDialogProps) {
    const [selectedOption, setSelectedOption] = React.useState<"allProjects" | "thisProject">("allProjects");
    const [showAllProjects, setShowAllProjects] = React.useState(false);
    const styles = {
        projectsSection: css({
            marginBottom: space[8],
        }),
        projectsContainer: css({
            display: "flex",
            flexWrap: "wrap",
            gap: space[4],
            marginTop: space[4],
        }),
    };
    // Fetch projects that use this ephemeral environment
    const projectsQuery = useQuery(async (repository: Repository) => await repository.EphemeralEnvironments.getProjectsForEphemeralEnvironments(project.SpaceId, environment.Id), [project.SpaceId, environment.Id], "Get projects for ephemeral environment");
    const { execute: deprovisionEnvironment, error: deprovisionError } = useMutation({
        name: "Deprovision ephemeral environment",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.deprovision(project.SpaceId, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    const { execute: deprovisionEnvironmentForProject, error: deprovisionForProjectError } = useMutation({
        name: "Deprovision ephemeral environment for this project",
        action: async (repository: Repository, environment: ProjectEphemeralEnvironmentDetail) => await repository.EphemeralEnvironments.deprovisionForProject(project.SpaceId, project.Id, environment.Id),
        afterComplete: async () => reloadEnvironmentsForProject(),
    });
    // Use the mutation's built-in error state
    const currentError = deprovisionError || deprovisionForProjectError;
    const handleDeprovision = async (): Promise<boolean> => {
        try {
            if (selectedOption === "allProjects") {
                await deprovisionEnvironment(environment);
            }
            else {
                await deprovisionEnvironmentForProject(environment);
            }
            return true; // Allow dialog to close on success
        }
        catch (error) {
            return false; // Keep dialog open on error
        }
    };
    const handleOptionChange = (value: "allProjects" | "thisProject") => setSelectedOption(value);
    // Don't render the dialog until projects data is loaded
    if (!projectsQuery.result) {
        return null;
    }
    const projects = projectsQuery.result.Projects || [];
    const projectCount = projects.length;
    const getProjectsToShow = () => {
        if (showAllProjects) {
            return projects;
        }
        return projects.slice(0, 5);
    };
    const hasMoreProjects = projectCount > 5;
    const remainingCount = projectCount - 5;
    return (<OkDialogLayout title={`Deprovision ${environment.Name}`} onOkClick={handleDeprovision} okButtonLabel="Deprovision" okButtonImportance="destructive" cancelButtonLabel="Cancel" errors={currentError ? [currentError] : null}>
            <div>
                {projectCount > 1 && (<div className={styles.projectsSection}>
                        <p>This environment is used in the projects below:</p>
                        <div className={styles.projectsContainer}>
                            {getProjectsToShow().map((project: ProjectIdAndName) => (<ProjectChip key={project.ProjectId} projectName={project.ProjectName}/>))}
                        </div>
                        {hasMoreProjects && <Button importance="quiet" onClick={() => setShowAllProjects(!showAllProjects)} label={showAllProjects ? "Show Summary" : `+${remainingCount} projects`}/>}
                    </div>)}

                <RadioButtonGroup title="Deprovisioning options:" value={selectedOption} onChange={handleOptionChange}>
                    <RadioButton value="allProjects" label={projectCount === 1 ? "Delete environment after deprovisioning" : "Deprovision for all projects and delete environment"}/>
                    <RadioButton value="thisProject" label={projectCount === 1 ? "Keep environment after deprovisioning" : "Deprovision for this project only and keep environment"} disabled={environment.Status == "Deprovisioned"}/>
                    {projectCount === 1 && <Note>The environment will continue to exist in Octopus but all associated infrastructure and resources will be removed as part of deprovisioning.</Note>}
                </RadioButtonGroup>
            </div>
        </OkDialogLayout>);
}
